import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Moon, Sun, Menu, X, ChevronDown, Linkedin, Search, ArrowRight } from "lucide-react";
import { SERVICES, MARKETS, ISSUES, DISCLAIMER_I18N, UI, LANG, setLang } from "../data/content";
import { useLatestArticles } from "./kit";
import { CookieConsent } from "./CookieConsent";
import { fmtDate } from "../lib/api";

const AXES = [
  { id: "services", label: UI.nav.services, hub: "/services", children: SERVICES.map((s) => ({ to: `/services/${s.slug}`, title: s.name, desc: s.proposition })) },
  { id: "markets", label: UI.nav.markets, hub: "/markets", children: MARKETS.map((m) => ({ to: `/markets/${m.slug}`, title: m.name, desc: m.proposition })) },
  { id: "issues", label: UI.nav.issues, hub: "/issues", children: ISSUES.map((u) => ({ to: `/issues/${u.slug}`, title: u.name, desc: u.card })) },
];

const useTheme = () => {
  const [dark, setDark] = useState(() => {
    const stored = localStorage.getItem("kairos_theme");
    if (stored) return stored === "dark";
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  });
  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("kairos_theme", dark ? "dark" : "light");
  }, [dark]);
  return [dark, setDark];
};

const Nav = () => {
  const [dark, setDark] = useTheme();
  const [open, setOpen] = useState(false);
  const [mega, setMega] = useState(null);
  const loc = useLocation();
  const featured = useLatestArticles(1)[0];
  useEffect(() => { setOpen(false); setMega(null); }, [loc.pathname]);

  return (
    <nav className="sticky top-0 z-50 bg-paper border-b border-line" data-testid="main-navbar">
      <div className="hidden lg:block border-b border-line">
        <div className="k-container flex justify-end items-center gap-6 py-1.5">
          <div className="flex gap-1 mr-2" data-testid="lang-switcher">
            <button onClick={() => LANG !== "en" && setLang("en")} className={`utility-link bg-transparent border-0 cursor-pointer ${LANG === "en" ? "!text-[var(--brand)] font-semibold" : ""}`} data-testid="lang-en-btn">EN</button>
            <span className="utility-link">·</span>
            <button onClick={() => LANG !== "el" && setLang("el")} className={`utility-link bg-transparent border-0 cursor-pointer ${LANG === "el" ? "!text-[var(--brand)] font-semibold" : ""}`} data-testid="lang-el-btn">ΕΛ</button>
          </div>
          <Link to="/insights" className="utility-link flex items-center gap-1.5" data-testid="utility-search"><Search size={12} /> {UI.nav.search}</Link>
          <Link to="/insights#subscribe" className="utility-link" data-testid="utility-subscribe">{UI.nav.subscribe}</Link>
          <Link to="/contact-us" className="utility-link" data-testid="utility-contact">{UI.nav.contactUs}</Link>
        </div>
      </div>
      <div className="k-container relative">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-baseline gap-2 no-underline py-4" data-testid="nav-logo">
            <span className="text-xl font-bold tracking-tight text-ink">KairosHK</span>
            <span className="hidden sm:inline text-[10px] uppercase tracking-[0.2em] text-ink-muted">{UI.nav.brandSuffix}</span>
          </Link>

          <div className="hidden lg:flex items-center gap-8" onMouseLeave={() => setMega(null)}>
            {AXES.map((axis) => (
              <button key={axis.id}
                className={`nav-link ${loc.pathname.startsWith(axis.hub) ? "active" : ""}`}
                data-open={mega === axis.id}
                onMouseEnter={() => setMega(axis.id)}
                onClick={() => setMega(mega === axis.id ? null : axis.id)}
                data-testid={`nav-${axis.id}-trigger`}>
                {axis.label} <ChevronDown size={14} />
              </button>
            ))}
            <NavLink to="/insights" className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`} onMouseEnter={() => setMega(null)} data-testid="nav-insights">{UI.nav.insights}</NavLink>
            <NavLink to="/about-us" className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`} onMouseEnter={() => setMega(null)} data-testid="nav-about">{UI.nav.aboutUs}</NavLink>
          </div>

          <div className="flex items-center gap-3">
            <button onClick={() => setDark(!dark)} aria-label="Toggle theme" className="p-2 text-ink-muted hover:text-ink transition-colors bg-transparent border-0 cursor-pointer" data-testid="theme-toggle-btn">
              {dark ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <Link to="/contact-us" className="btn-primary hidden md:inline-flex !py-2.5 !px-5 text-sm" data-testid="nav-contact-btn">{UI.nav.contactUs}</Link>
            <button onClick={() => setOpen(!open)} className="lg:hidden p-2 text-ink bg-transparent border-0 cursor-pointer" aria-label="Menu" data-testid="mobile-menu-trigger">
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {mega && (
          <motion.div initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.15 }}
            className="mega-panel hidden lg:block" onMouseEnter={() => setMega(mega)} onMouseLeave={() => setMega(null)} data-testid={`mega-panel-${mega}`}>
            <div className="k-container grid grid-cols-12 gap-8 py-8">
              {(() => {
                const axis = AXES.find((a) => a.id === mega);
                return (
                  <>
                    <div className="col-span-3 border-r border-line pr-8">
                      <p className="eyebrow mb-3">{axis.label}</p>
                      <Link to={axis.hub} className="arrow-link" data-testid={`mega-hub-${axis.id}`}>{UI.nav.viewAll} <ArrowRight size={14} className="arr" /></Link>
                    </div>
                    <div className="col-span-6 grid grid-cols-2 gap-1">
                      {axis.children.map((c) => (
                        <Link key={c.to} to={c.to} className="mega-item" data-testid={`mega-item-${c.to.split("/").pop()}`}>
                          <span className="mega-title block text-sm font-semibold text-ink transition-colors">{c.title}</span>
                          <span className="block text-xs text-ink-muted mt-1 leading-snug">{c.desc.length > 90 ? c.desc.slice(0, 90) + "…" : c.desc}</span>
                        </Link>
                      ))}
                    </div>
                    <div className="col-span-3 border-l border-line pl-8">
                      {featured && (
                        <Link to={`/insights/${featured.slug}`} className="no-underline" data-testid="mega-featured-insight">
                          <p className="eyebrow mb-3">{UI.nav.featuredInsight}</p>
                          <p className="text-sm font-semibold text-ink leading-snug">{featured.title}</p>
                          <p className="text-xs text-ink-muted mt-2" style={{ fontVariantNumeric: "tabular-nums" }}>{fmtDate(featured.publishedAt)}</p>
                        </Link>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ height: 0 }} animate={{ height: "auto" }} exit={{ height: 0 }} className="lg:hidden overflow-hidden border-t border-line bg-paper" data-testid="mobile-menu">
            <div className="k-container py-6 flex flex-col gap-1">
              {AXES.map((axis) => (
                <div key={axis.id} className="mb-3">
                  <Link to={axis.hub} className="text-sm font-semibold text-ink no-underline">{axis.label}</Link>
                  <div className="flex flex-col mt-1">
                    {axis.children.map((c) => (
                      <Link key={c.to} to={c.to} className="text-sm text-ink-muted no-underline py-1.5 pl-4">{c.title}</Link>
                    ))}
                  </div>
                </div>
              ))}
              <Link to="/insights" className="text-sm font-semibold text-ink no-underline py-1.5">{UI.nav.insights}</Link>
              <Link to="/about-us" className="text-sm font-semibold text-ink no-underline py-1.5">{UI.nav.aboutUs}</Link>
              <div className="flex items-center gap-3 mt-4 pt-4 border-t border-line" data-testid="mobile-lang-switcher">
                <button onClick={() => LANG !== "en" && setLang("en")}
                  className={`text-sm bg-transparent border border-line px-4 py-2 cursor-pointer ${LANG === "en" ? "font-semibold text-[var(--brand)] !border-[var(--accent)]" : "text-ink-muted"}`}
                  data-testid="mobile-lang-en-btn">English</button>
                <button onClick={() => LANG !== "el" && setLang("el")}
                  className={`text-sm bg-transparent border border-line px-4 py-2 cursor-pointer ${LANG === "el" ? "font-semibold text-[var(--brand)] !border-[var(--accent)]" : "text-ink-muted"}`}
                  data-testid="mobile-lang-el-btn">Ελληνικά</button>
              </div>
              <Link to="/contact-us" className="btn-primary self-start mt-4" data-testid="mobile-contact-btn">{UI.nav.contactUs}</Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

const FooterCol = ({ title, links }) => (
  <div>
    <h4 className="footer-col-title">{title}</h4>
    <ul className="space-y-2.5 list-none p-0 m-0">
      {links.map(([label, to]) => (
        <li key={to + label}><Link to={to}>{label}</Link></li>
      ))}
    </ul>
  </div>
);

const Footer = () => (
  <>
    <section className="pre-footer" data-testid="pre-footer-band">
      <div className="k-container py-14 flex flex-col md:flex-row md:items-center justify-between gap-8">
        <div>
          <h2 className="k-h2 !text-white">{UI.prefooter.title}</h2>
          <p className="mt-2 text-sm" style={{ color: "rgba(255,255,255,0.85)" }}>{UI.prefooter.lead}</p>
        </div>
        <div className="flex gap-4 shrink-0">
          <Link to="/contact-us" className="btn-inverse" data-testid="prefooter-contact-btn">{UI.prefooter.contact}</Link>
          <Link to="/insights#subscribe" className="btn-secondary !text-white !border-white hover:!bg-white/10" data-testid="prefooter-subscribe-btn">{UI.prefooter.subscribe}</Link>
        </div>
      </div>
    </section>
    <footer className="site-footer" data-testid="site-footer">
      <div className="k-container pt-16 pb-8">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-10 mb-14">
          <FooterCol title={UI.nav.services} links={[[UI.nav.viewAll, "/services"], ...SERVICES.map((s) => [s.name, `/services/${s.slug}`])]} />
          <FooterCol title={UI.nav.markets} links={[[UI.nav.viewAll, "/markets"], ...MARKETS.map((m) => [m.name, `/markets/${m.slug}`])]} />
          <FooterCol title={UI.nav.issues} links={[[UI.nav.viewAll, "/issues"], ...ISSUES.map((u) => [u.name, `/issues/${u.slug}`])]} />
          <FooterCol title={UI.nav.insights} links={[[UI.home.allInsights, "/insights"], [UI.funding.navProgrammes, "/funding"], [UI.funding.navExplorer, "/funding/explorer"], [UI.footer.sources, "/sources"], [UI.footer.topicReg, "/insights/topics/eu-financial-regulation"], [UI.footer.topicFunding, "/insights/topics/eu-funding"]]} />
          <FooterCol title={UI.nav.aboutUs} links={[[UI.nav.aboutUs, "/about-us"], [UI.nav.contactUs, "/contact-us"], [UI.footer.privacy, "/privacy"], [UI.footer.legal, "/legal"], [UI.footer.cookies, "/cookies"]]} />
        </div>
        <div className="flex items-center gap-4 mb-8">
          <a href="https://www.linkedin.com/" target="_blank" rel="noopener noreferrer" aria-label="KairosHK on LinkedIn" data-testid="footer-linkedin-link"><Linkedin size={18} /></a>
        </div>
        <p className="text-xs leading-relaxed mb-6" style={{ color: "rgba(255,255,255,0.55)" }} data-testid="footer-disclaimer">{DISCLAIMER_I18N}</p>
        <div className="flex flex-col md:flex-row justify-between gap-3 text-xs pt-6" style={{ borderTop: "1px solid rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.55)", fontVariantNumeric: "tabular-nums" }}>
          <span data-testid="footer-company-line">{UI.footer.companyLine}<br /><span data-testid="footer-reg-office">{UI.footer.regOffice}</span></span>
          <span>© {new Date().getFullYear()} Kairos Trade Limited. {UI.footer.rights}</span>
        </div>
      </div>
    </footer>
  </>
);

export default function Layout({ children }) {
  return (
    <div className="min-h-screen flex flex-col bg-paper">
      <Nav />
      <main className="flex-1">{children}</main>
      <Footer />
      <CookieConsent />
    </div>
  );
}
